import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Skeleton
} from '@mui/material';
import {
    Inventory as InventoryIcon,
    PointOfSale as PointOfSaleIcon,
    Build as BuildIcon,
    PhotoLibrary as PhotoLibraryIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import { httpClient } from '../../../common/httpclient';
import { useSearchParams } from 'react-router-dom';

// Componente Skeleton para Cards
const CardSkeleton = ({ title, icon }) => (
    <Card
        elevation={0}
        sx={{
            height: '100%',
            borderRadius: 3,
            boxShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.3)'
                : '0 4px 20px rgba(0,0,0,0.08)',
            background: (theme) => theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            border: (theme) => theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.1)'
                : '1px solid rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease',
            '&:hover': {
                boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 6px 25px rgba(0,0,0,0.4)'
                    : '0 6px 25px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
            }
        }}
    >
        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                    p: 0.5,
                    borderRadius: 0.75,
                    bgcolor: 'primary.main',
                    mr: 0.75,
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0 2px 6px rgba(0,0,0,0.3)'
                        : '0 2px 6px rgba(0,0,0,0.15)'
                }}>
                    {icon}
                </Box>
                <Skeleton variant="text" width={120} height={14} />
            </Box>
            <Skeleton variant="text" width={80} height={32} sx={{ mt: 'auto' }} />
        </CardContent>
    </Card>
);

// Componente MetricCard
const MetricCard = ({ title, value, icon, color, loading = false }) => {
    const theme = useTheme();

    if (loading) {
        return <CardSkeleton title={title} icon={icon} />;
    }

    return (
        <Card elevation={0} sx={{
            height: '100%',
            borderRadius: 3,
            boxShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.3)'
                : '0 4px 20px rgba(0,0,0,0.08)',
            background: (theme) => theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            border: (theme) => theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.1)'
                : '1px solid rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease',
            '&:hover': {
                boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 6px 25px rgba(0,0,0,0.4)'
                    : '0 6px 25px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)'
            }
        }}>
            <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box sx={{
                        p: 0.5,
                        borderRadius: 0.75,
                        bgcolor: color,
                        mr: 0.75,
                        boxShadow: theme.palette.mode === 'dark'
                            ? '0 2px 6px rgba(0,0,0,0.3)'
                            : '0 2px 6px rgba(0,0,0,0.15)'
                    }}>
                        {icon}
                    </Box>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.2,
                            fontSize: '0.65rem',
                            lineHeight: 1.2
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1.1,
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                        mt: 'auto',
                        textAlign: 'left'
                    }}
                >
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default function SubscriptionResources() {
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const enterpriseId = searchParams.get('enterpriseId');
    const [loading, setLoading] = useState(true);
    const [resources, setResources] = useState(null);
    const [error, setError] = useState(null);

    const fetchResources = async () => {
        if (!enterpriseId) {
            setError('ID da empresa não fornecido');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await httpClient.get(`/admin/subscription/resources/${enterpriseId}`);
            
            if (response.status === 200 && response.data) {
                setResources(response.data);
            } else {
                setError('Erro ao carregar recursos da assinatura');
            }
        } catch (error) {
            console.error('Error fetching subscription resources:', error);
            setError('Erro ao carregar recursos da assinatura');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, [enterpriseId]);

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return new Intl.NumberFormat('pt-BR').format(num);
    };

    return (
        <PageScaffold links={[]} titlePageTarget={'Recursos da Assinatura'}>
            <Box sx={{ 
                width: '100%',
                maxWidth: 1400,
                mx: 'auto',
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 3 }
            }}>
                {/* Header */}
                <Box sx={{ 
                    mb: 4, 
                    textAlign: 'center'
                }}>
                    <Typography 
                        variant="h4" 
                        component="h1"
                        sx={{ 
                            fontWeight: 700,
                            mb: 1,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '1.8rem', md: '2.2rem' }
                        }}
                    >
                        Recursos da Assinatura
                    </Typography>
                    <Typography 
                        variant="body1"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            maxWidth: 600,
                            mx: 'auto'
                        }}
                    >
                        Visualize o uso dos recursos da sua assinatura
                    </Typography>
                </Box>

                {/* Cards de Métricas */}
                {error ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        color: 'error.main'
                    }}>
                        <Typography variant="body1">{error}</Typography>
                    </Box>
                ) : (
                    <Box sx={{ 
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(2, 1fr)',
                            lg: 'repeat(3, 1fr)',
                            xl: 'repeat(5, 1fr)'
                        },
                        gap: 3
                    }}>
                        <MetricCard
                            title="Total de Produtos Cadastrados"
                            value={formatNumber(resources?.total_products || 0)}
                            icon={<InventoryIcon sx={{ color: 'white', fontSize: 20 }} />}
                            color="primary.main"
                            loading={loading}
                        />
                        <MetricCard
                            title="Total de Vendas Realizadas"
                            value={formatNumber(resources?.total_sales || 0)}
                            icon={<PointOfSaleIcon sx={{ color: 'white', fontSize: 20 }} />}
                            color="success.main"
                            loading={loading}
                        />
                        <MetricCard
                            title="Total de Ordens de Serviços Geradas"
                            value={formatNumber(resources?.total_service_orders || 0)}
                            icon={<BuildIcon sx={{ color: 'white', fontSize: 20 }} />}
                            color="warning.main"
                            loading={loading}
                        />
                        <MetricCard
                            title="Total de Imagens de Produtos"
                            value={formatNumber(resources?.total_product_images || 0)}
                            icon={<PhotoLibraryIcon sx={{ color: 'white', fontSize: 20 }} />}
                            color="info.main"
                            loading={loading}
                        />
                        <MetricCard
                            title="Total de Usuários Cadastrados"
                            value={formatNumber(resources?.total_users || 0)}
                            icon={<PeopleIcon sx={{ color: 'white', fontSize: 20 }} />}
                            color="secondary.main"
                            loading={loading}
                        />
                    </Box>
                )}
            </Box>
        </PageScaffold>
    );
}

